import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, FlaskConical, Award, GitBranch, Gauge, HelpCircle } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import ElevateBadge from "../components/ElevateBadge";
import ResearchDeepDive from "../components/ResearchDeepDive";
import { useWaitlist } from "../context/WaitlistContext";

const pillars = [
  {
    tag: "UPII",
    title: "Unified Personal Intelligence Infrastructure",
    desc: "A private, sovereign memory substrate for individuals — a typed, addressable, permissioned context graph any model can bind to, with consent, that lives on the user's own device.",
  },
  {
    tag: "UEII",
    title: "Unified Enterprise Intelligence Infrastructure",
    desc: "The same substrate, governed for organisations — role-based access, data lineage, and cross-functional learning loops that turn one team's insight into shared institutional memory.",
  },
  {
    tag: "Substrate",
    title: "The local memory substrate",
    desc: "The engine beneath both: content-addressed capture, multi-tier storage, and hybrid retrieval — sovereign by construction, verifiable, and offline by default.",
  },
];

const proof = [
  {
    icon: Award,
    t: "Provisional patent",
    d: "On multi-signal context rehydration — fusing semantic, temporal and relational signals into one ranked context window.",
  },
  {
    icon: GitBranch,
    t: "Reproducible by design",
    d: "Re-ingesting the same corpus reproduces 100% of chunk hashes. Citations stay stable across re-indexes and re-embeddings.",
  },
  {
    icon: Gauge,
    t: "Benchmarked retrieval",
    d: "Recall@10 = 0.958 on a committed labelled set — with a public harness so anyone can reproduce the number.",
  },
  {
    icon: HelpCircle,
    t: "Six open questions",
    d: "From the right unit of memory promotion to provenance under inference — the questions that decide whether the substrate is real.",
  },
];

export default function About() {
  const { openWaitlist } = useWaitlist();

  return (
    <div className="relative overflow-hidden bg-ink">
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5 pt-16 pb-20 md:pt-24">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="absolute -left-40 top-10 h-[420px] w-[420px] bg-radial-ember blur-3xl opacity-70" />
        <div className="container-x relative">
          <Reveal>
            <p className="overline">
              About ·{" "}
              <a
                href="https://datafrontier.co"
                target="_blank"
                rel="noreferrer"
                className="transition-colors hover:text-paper"
              >
                DataFrontier Innovations
              </a>
            </p>
          </Reveal>
          <Reveal delay={0.06}>
            <h1 className="mt-5 max-w-4xl font-display text-5xl font-light leading-[1.05] tracking-tight text-paper md:text-7xl">
              Built as research.
              <br />
              Shipped as <span className="italic text-ember">product.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-7 max-w-2xl text-lg font-light leading-relaxed text-mute">
              Smritium is developed by{" "}
              <a
                href="https://datafrontier.co"
                target="_blank"
                rel="noreferrer"
                className="text-paper underline decoration-ember/50 underline-offset-2 transition-colors hover:text-ember"
              >
                DataFrontier Innovations
              </a>{" "}
              — a research-led AI lab in Bengaluru building the sovereign memory
              layer for AI: the private, portable context substrate (UPII) for
              individuals and its governed counterpart (UEII) for enterprises. We
              publish what we learn, and productise what compounds.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-8 flex flex-wrap gap-4">
              <button onClick={() => openWaitlist()} className="btn-primary" data-testid="about-waitlist-button">
                Join the Waitlist <ArrowRight size={16} />
              </button>
              <Link to="/" className="btn-ghost" data-testid="about-home-button">
                Back to Smritium
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THESIS */}
      <section className="py-24 md:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <p className="overline">The thesis</p>
            <h2 className="mt-4 font-display text-3xl font-light leading-tight tracking-tight text-paper md:text-[2.6rem]">
              Models are no longer the hard part.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-6 text-lg font-light leading-relaxed text-mute">
              <p>
                The hard problems now live in everything around the model —
                memory, identity, and the substrate between a person (or an
                enterprise) and the agents that act for them. That is where we
                work.
              </p>
              <p>
                Our core claim: personal and institutional context is a{" "}
                <span className="text-paper">primitive, not a feature</span> — a
                typed, addressable, permissioned store of who you are, what you've
                done, and what you're working on, that any model can bind to with
                consent, the way applications bind to a filesystem rather than
                reinvent one.
              </p>
              <p>
                Smritium is the first product expression of that research: a
                cross-vendor, local-first memory substrate that tests the thesis
                with a single sitting of real user experience.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="border-t border-white/5 bg-indigo/10 py-24 md:py-28">
        <div className="container-x">
          <Reveal>
            <p className="overline">What we build</p>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-light leading-tight tracking-tight text-paper md:text-[2.6rem]">
              Two substrates. One memory layer.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {pillars.map((p, i) => (
              <Reveal key={p.tag} delay={i * 0.06}>
                <div className="card card-hover h-full">
                  <div className="flex items-center gap-2">
                    <FlaskConical size={16} className="text-ember" />
                    <span className="font-mono text-xs uppercase tracking-overline text-ember">
                      {p.tag}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-xl text-paper">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-mute">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ResearchDeepDive />

      {/* PROOF / CUTTING EDGE */}
      <section className="py-24 md:py-28">
        <div className="container-x">
          <Reveal>
            <p className="overline">Cutting-edge — and measured</p>
            <h2 className="mt-4 max-w-3xl font-display text-3xl font-light leading-tight tracking-tight text-paper md:text-[2.6rem]">
              Claims we can reproduce, not just assert.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {proof.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.05}>
                <div className="card card-hover h-full">
                  <p.icon className="text-ember" size={24} />
                  <h3 className="mt-4 font-display text-xl text-paper">{p.t}</h3>
                  <p className="mt-2 leading-relaxed text-mute">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ELEVATE + CTA */}
      <section className="border-t border-white/5 py-24 md:py-28">
        <div className="container-x">
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-void/60 p-8 md:p-12">
              <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
                <div>
                  <ElevateBadge variant="card" />
                  <p className="mt-6 leading-relaxed text-mute">
                    Smritium is developed under the ELEVATE Nxt programme,
                    Government of Karnataka — a deep-tech grant-in-aid initiative
                    supporting frontier research built in India.
                  </p>
                </div>
                <div>
                  <h3 className="font-display text-3xl font-light leading-tight text-paper">
                    Help shape a sovereign memory layer built in India.
                  </h3>
                  <div className="mt-7 flex flex-wrap gap-4">
                    <button onClick={() => openWaitlist()} className="btn-primary" data-testid="about-cta-waitlist">
                      Join the Waitlist <ArrowRight size={16} />
                    </button>
                    <a
                      href="https://www.datafrontier.co/research/upii"
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost"
                      data-testid="about-research-link"
                    >
                      Read the UPII note <ArrowUpRight size={16} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
